import {getNextBlockNode} from './getNextBlockNode'
import {getPreviousBlockNode} from './getPreviousBlockNode'

/**
 * Insert a block node in specified place.
 *
 * @param  {Transaction} tx
 * @param  {object} data           Node data object to create
 * @param  {string} mode           Mode, first, before, last, after
 * @param  {Object} [refNode=null] Reference node from where to insert new node
 */
function _insertBlockNode(tx, data, mode, refNode) {
    const strategy = _getInsertReference(tx, mode, refNode)

    const node = tx.create(data)
    tx.insertBlockNode(node)

    return {
        node: node,
        mode: strategy.mode,
        strategyRefNode: strategy.refNode
    }
}

/**
 * Calculate where we want to insert the new node in reference to another node
 * using the specified (or not specified) mode.
 *
 * This function then uses _getInsertStrategy() to make sure that the wanted
 * reference is not a temporary non transactionable node.
 *
 * @param {Transaction} tx      Existing transaction
 * @param {string}              [mode='after']    First, before, last, after or null
 * @param {object}              refNode Reference node or null
 * @return {{mode: string, refNode: object}}
 */
function _getInsertReference(tx, mode, refNode) {
    const doc = tx._editorSession.getDocument()
    const nodes = doc.getNodes()['body'].nodes

    switch(mode) {
        case 'first':
            refNode = nodes.length ? doc.get(nodes[0]) : null
            break

        case 'last':
            refNode = nodes.length ? doc.get(nodes[nodes.length - 1]) : null
            break

        case 'after':
            if (!refNode) {
                throw new Error('Inserting after a node requires the node parameter to be non empty')
            }
            break

        default:
            mode = 'after'
            if (!refNode) {
                const refSel = tx._editorSession.getSelection()
                const refSelSurfaceId = refSel.surfaceId.split('/')

                if (refSel.nodeId) {
                    // Get node from the path, ie for a block node
                    refNode = doc.get(refSel.nodeId)
                }
                else if (refSel && refSel.type === 'property' && refSelSurfaceId.length === 1) {
                    // Get node from selection path for text node
                    refNode = doc.get(refSel.getPath()[0])
                }
                else if (refSel && refSel.type === 'property' && refSelSurfaceId.length > 1) {
                    // Get top most node from full surface path, happens when selection
                    // is in a cell or another sub node.
                    refNode = doc.get(refSelSurfaceId[1])
                }
                else if (nodes.length > 0) {
                    // Fallback to last node
                    mode = 'last'
                    refNode = nodes.length ? doc.get(nodes[nodes.length - 1]) : null
                }
                else {
                    throw new Error('A document cannot be empty')
                }
            }
    }

    return _getInsertStrategy(tx, mode, refNode)
}

/**
 * Calculate exact insert stragey of block nodes. This is important so that
 * there is never left any references to selections of block nodes that are
 * "ignored", ie not saved in undo/redo transactions.
 *
 * Examples
 * 1. If block node is to be inserted after an "ignored/non transactionable"
 * block, the new block node must be inserted before the next valid block node.
 *
 * 2. If a block node is to be inserted before "ignored/non transactionable"
 * block, the new block must be inserted after the first preciding valid block node.
 *
 * @param {Transaction} tx
 * @param {string} mode - After, before, last or first
 * @param {object} refNode - The node of interest from wich insertion mode is calculated
 *
 * @return {object} - Object with new reference node and new mode to use
 */
function _getInsertStrategy(tx, mode, refNode) {
    if (refNode && refNode.ignorePosition !== true) {
        return {
            mode,
            refNode
        }
    }

    const editorSession = tx._editorSession
    if (mode === 'last') {
        const previousNode = getPreviousBlockNode(editorSession)
        return {
            mode: 'after',
            refNode: previousNode
        }
    }
    else if (mode === 'first') {
        const nextNode = getNextBlockNode(editorSession)
        return {
            mode: 'before',
            refNode: nextNode
        }
    }

    const previousNode = getPreviousBlockNode(editorSession, refNode)
    const nextNode = getNextBlockNode(editorSession, refNode)

    if (!previousNode && !nextNode) {
        throw new Error('Document contains no valid block nodes, bailing out')
    }

    if (mode === 'before') {
        if (previousNode) {
            return {
                mode: 'after',
                refNode: previousNode
            }
        }
        else {
            return {
                mode: 'before',
                refNode: nextNode
            }
        }
    }
    else {
        if (nextNode) {
            return {
                mode: 'before',
                refNode: nextNode
            }
        }
        else {
            return {
                mode: 'after',
                refNode: previousNode
            }

        }
    }
}

/**
 * Set a selection after or before a block node
 *
 * @param {Transaction} tx
 * @param {String} mode - Where to set selcection (after, before, first, last)
 * @param {Object} refNode - Reference node
 */
function _setSelection(tx, mode, refNode) {
    tx.setSelection({
        type: 'node',
        containerId: 'body',
        nodeId: refNode.id,
        mode: ['first', 'before'].includes(mode) ? 'before' : 'after'
    })
}

/**
 * Get all children node ids beneath a specified parent node
 *
 * @param {Object} editorSession
 * @param {Object} parentNodeId - The parent node
 *
 * @return {Array} - Array of ids
 */
function _getDocumentNodeIds(editorSession, parentNodeId) {
    const doc = editorSession.getDocument()
    const parentNode = doc.get(parentNodeId)

    return parentNode.nodes
}

export {
    _insertBlockNode,
    _getInsertReference,
    _getInsertStrategy,
    _getDocumentNodeIds,
    _setSelection
}
